import { api } from './client';
import type {
  AddToCartDto,
  ApplyDiscountCodeDto,
  ApplyGiftCardDto,
  CartDto,
  CheckoutDto,
  CheckoutResultDto,
  PayTrBinDetailDto,
  SetCartNoteDto,
  SetCartShippingDto,
  ShippingOptionDto,
  UpdateCartItemDto,
} from './types';

/* ------------------------------------------------------------------ *
 *  Sepet ve odeme — 14 uc
 *
 *  Misafir sepeti sunucudaki `cart-session` HttpOnly cerezine baglidir;
 *  istemcinin bir anahtar tasimasi gerekmez, yalnizca `withCredentials`
 *  acik olmalidir.
 * ------------------------------------------------------------------ */

export const cartApi = {
  /** 1. Sepeti getirir; yoksa olusturur. Tutarlar her cagrida yeniden hesaplanir. */
  get: () => api.get<CartDto>('/api/cart'),

  /** 2. Sepete urun ekler. Stok yetersizse 409 doner. */
  addItem: (dto: AddToCartDto) => api.post<CartDto>('/api/cart/items', dto),

  /** 3. Kalem adedini gunceller. */
  updateItem: (cartItemId: number, dto: UpdateCartItemDto) =>
    api.put<CartDto>(`/api/cart/items/${cartItemId}`, dto),

  /** 4. Kalemi cikarir. */
  removeItem: (cartItemId: number) => api.delete<CartDto>(`/api/cart/items/${cartItemId}/remove`),

  /** 5. Sepeti bosaltir; rezervasyonlar da serbest kalir. */
  clear: () => api.delete<CartDto>('/api/cart'),

  /** 6. Indirim kodu uygular. */
  applyDiscountCode: (dto: ApplyDiscountCodeDto) =>
    api.post<CartDto>('/api/cart/discount-code', dto),

  /** 7. Indirim kodunu kaldirir. */
  removeDiscountCode: (code: string) =>
    api.delete<CartDto>(`/api/cart/discount-code/${encodeURIComponent(code)}`),

  /** 8. Hediye kartini sepete tanimlar (odeme yontemi, indirim degil). */
  applyGiftCard: (dto: ApplyGiftCardDto) => api.post<CartDto>('/api/cart/gift-card', dto),

  /** 9. Hediye kartini kaldirir. */
  removeGiftCard: (giftCardId: number) =>
    api.delete<CartDto>(`/api/cart/gift-card/${giftCardId}`),

  /** 10. Teslimat adresini ve kargo yontemini belirler. */
  setShipping: (dto: SetCartShippingDto) => api.put<CartDto>('/api/cart/shipping', dto),

  /** 11. Adrese uygun kargo secenekleri ve ucretleri. */
  shippingOptions: () => api.get<ShippingOptionDto[]>('/api/cart/shipping-options'),

  /** 12. Siparis notu. */
  setNote: (dto: SetCartNoteDto) => api.put<CartDto>('/api/cart/note', dto),

  /** 13. Giris sonrasi misafir sepetini uye sepetiyle birlestirir. */
  merge: () => api.post<CartDto>('/api/cart/merge'),

  /**
   * 14. Siparisi olusturur.
   *
   * `Idempotency-Key` zorunlu degildir ama siddetle onerilir: ag koparsa
   * ayni anahtarla tekrarlanan istek yeni siparis acmaz, ilkini dondurur.
   */
  checkout: (dto: CheckoutDto, idempotencyKey?: string) =>
    api.post<CheckoutResultDto>('/api/checkout', dto, {
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    }),
};

export const paymentsApi = {
  getStatus: (orderPublicId: string) =>
    api.get<{
      orderPublicId: string;
      orderNumber: string;
      financialStatus: string;
      status: string;
      isPaid: boolean;
      paidAt?: string | null;
    }>(`/api/payments/status/${orderPublicId}`),
  cancelDraft: (orderPublicId: string) =>
    api.post<{ success: boolean }>(`/api/checkout/cancel-draft/${orderPublicId}`),
  getBinDetail: (bin: string) =>
    api.get<PayTrBinDetailDto>(`/api/payments/paytr/bin-detail?bin=${encodeURIComponent(bin)}`),
};

/** Odeme denemesi icin benzersiz anahtar uretir. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
